import OpportunitiesAPI from '../../api/opportunities';

const state = {
  records: [],
  kanbanData: null,
  currentOpportunity: null,
  leadSources: [],
  procedures: [],
  uiFlags: {
    isFetching: false,
    isFetchingKanban: false,
    isFetchingDetail: false,
    isCreating: false,
    isMoving: false,
    isCreatingActivity: false,
  },
  meta: { totalCount: 0 },
};

const getters = {
  getOpportunities: $state => $state.records,
  getKanbanData: $state => $state.kanbanData,
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
  getOpportunityById: $state => id =>
    $state.records.find(o => o.id === Number(id)),
  getCurrentOpportunity: $state => $state.currentOpportunity,
  getLeadSources: $state => $state.leadSources,
  getProcedures: $state => $state.procedures,
};

const mutations = {
  SET_OPPORTUNITIES($state, { records, meta }) {
    $state.records = records;
    $state.meta = meta || { totalCount: 0 };
  },
  SET_KANBAN_DATA($state, data) {
    $state.kanbanData = data;
  },
  ADD_OPPORTUNITY($state, opp) {
    $state.records.unshift(opp);
  },
  UPDATE_OPPORTUNITY($state, opp) {
    const index = $state.records.findIndex(o => o.id === opp.id);
    if (index !== -1) $state.records.splice(index, 1, opp);
  },
  DELETE_OPPORTUNITY($state, id) {
    $state.records = $state.records.filter(o => o.id !== id);
  },
  MOVE_OPPORTUNITY_STAGE($state, { id, pipeline_stage_id, kanban_order }) {
    // Optimistic update in kanban data
    if (!$state.kanbanData?.groups) return;
    let movedItem = null;

    $state.kanbanData.groups.forEach(group => {
      const idx = group.items.findIndex(item => item.id === id);
      if (idx !== -1) {
        movedItem = group.items.splice(idx, 1)[0];
        group.total -= 1;
      }
    });

    if (movedItem) {
      const targetGroup = $state.kanbanData.groups.find(
        g => g.id === pipeline_stage_id
      );
      if (targetGroup) {
        movedItem.kanban_order = kanban_order;
        targetGroup.items.push(movedItem);
        targetGroup.total += 1;
      }
    }
  },
  // "Carregar mais" de UMA coluna: anexa os cards da próxima página no stage,
  // sem duplicar (dedupe por id) e sem recarregar o board inteiro.
  APPEND_STAGE_ITEMS($state, { stageId, items }) {
    if (!$state.kanbanData?.groups || !items?.length) return;
    const group = $state.kanbanData.groups.find(g => g.id === stageId);
    if (!group) return;
    const seen = new Set((group.items || []).map(i => i.id));
    group.items.push(...items.filter(i => !seen.has(i.id)));
  },
  SET_CURRENT_OPPORTUNITY($state, opp) {
    $state.currentOpportunity = opp;
  },
  ADD_ACTIVITY($state, activity) {
    // Payload sem id (ex.: resposta vazia do backend) não entra na lista —
    // era isso que rendia o card fantasma na aba Atividades.
    if (!activity?.id || !$state.currentOpportunity) return;
    if (!$state.currentOpportunity.activities) {
      $state.currentOpportunity.activities = [];
    }
    $state.currentOpportunity.activities.unshift(activity);
  },
  REMOVE_ACTIVITY($state, activityId) {
    if ($state.currentOpportunity?.activities) {
      $state.currentOpportunity.activities =
        $state.currentOpportunity.activities.filter(a => a.id !== activityId);
    }
  },
  SET_UI_FLAG($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
  SET_LEAD_SOURCES($state, sources) {
    $state.leadSources = Array.isArray(sources) ? sources : [];
  },
  SET_PROCEDURES($state, procedures) {
    $state.procedures = Array.isArray(procedures) ? procedures : [];
  },
};

const actions = {
  async fetch({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await OpportunitiesAPI.get(params);
      commit('SET_OPPORTUNITIES', {
        records: data.payload,
        meta: data.meta,
      });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchKanban({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetchingKanban: true });
    try {
      const { data } = await OpportunitiesAPI.kanban(params);
      commit('SET_KANBAN_DATA', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingKanban: false });
    }
  },

  // Busca a próxima página de UMA coluna e anexa (não substitui o board).
  // `params` já vem com o page + todos os filtros do PipelineKanban.
  async loadMoreStage({ commit }, { stageId, params }) {
    const { data } = await OpportunitiesAPI.kanban(params);
    const group = (data.groups || []).find(g => g.id === stageId);
    if (group?.items?.length) {
      commit('APPEND_STAGE_ITEMS', { stageId, items: group.items });
    }
    return group?.items?.length || 0;
  },

  async create({ commit }, opportunityData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await OpportunitiesAPI.create(opportunityData);
      commit('ADD_OPPORTUNITY', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, ...updateData }) {
    try {
      const { data } = await OpportunitiesAPI.update(id, updateData);
      commit('UPDATE_OPPORTUNITY', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  // Transferir para outro pipeline: o card some do kanban atual (868ka5bvq)
  async transfer({ commit }, { id, targetPipelineId }) {
    const { data } = await OpportunitiesAPI.transfer(id, targetPipelineId);
    commit('DELETE_OPPORTUNITY', id);
    return data;
  },

  async moveStage({ commit }, { id, pipeline_stage_id, kanban_order }) {
    commit('SET_UI_FLAG', { isMoving: true });
    // Optimistic update
    commit('MOVE_OPPORTUNITY_STAGE', { id, pipeline_stage_id, kanban_order });
    try {
      await OpportunitiesAPI.moveStage(id, {
        pipeline_stage_id,
        kanban_order,
      });
    } catch (error) {
      // TODO: revert optimistic update on error
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isMoving: false });
    }
  },

  // Etiquetas do card (POST substitui a lista inteira, como na conversa).
  // Refaz o fetchOne para o painel aberto refletir na hora.
  async updateLabels({ dispatch }, { id, labels }) {
    await OpportunitiesAPI.updateLabels(id, labels);
    return dispatch('fetchOne', id);
  },

  // Lista de Espera: "Agendado" — fecha o card na fila e move/cria no pipeline principal.
  async waitlistSchedule(_, { id }) {
    const { data } = await OpportunitiesAPI.waitlistSchedule(id);
    return data;
  },

  async delete({ commit }, id) {
    try {
      await OpportunitiesAPI.delete(id);
      commit('DELETE_OPPORTUNITY', id);
    } catch (error) {
      throw error;
    }
  },

  async archive({ commit }, id) {
    await OpportunitiesAPI.archive(id);
    // some do kanban (scope :active filtra archived=false)
    commit('DELETE_OPPORTUNITY', id);
  },

  async unarchive(_ctx, id) {
    await OpportunitiesAPI.unarchive(id);
  },

  async fetchOne({ commit }, id) {
    commit('SET_UI_FLAG', { isFetchingDetail: true });
    try {
      const { data } = await OpportunitiesAPI.show(id);
      commit('SET_CURRENT_OPPORTUNITY', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingDetail: false });
    }
  },

  async createActivity({ commit }, { opportunityId, activityData }) {
    commit('SET_UI_FLAG', { isCreatingActivity: true });
    try {
      const { data } = await OpportunitiesAPI.createActivity(
        opportunityId,
        activityData
      );
      commit('ADD_ACTIVITY', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreatingActivity: false });
    }
  },

  async deleteActivity({ commit }, { opportunityId, activityId }) {
    try {
      await OpportunitiesAPI.deleteActivity(opportunityId, activityId);
      commit('REMOVE_ACTIVITY', activityId);
    } catch (error) {
      throw error;
    }
  },

  // Origens + procedimentos gerenciáveis da conta (868ka5bcr / 868ka5bg6) —
  // ambos vêm de /crm_settings numa chamada só.
  async fetchLeadSources({ commit }) {
    const { data } = await OpportunitiesAPI.getCrmSettings();
    commit('SET_LEAD_SOURCES', data.lead_sources);
    commit('SET_PROCEDURES', data.procedures);
    return data.lead_sources;
  },

  async updateLeadSources({ commit }, sources) {
    const { data } = await OpportunitiesAPI.updateCrmSettings({
      lead_sources: sources,
    });
    commit('SET_LEAD_SOURCES', data.lead_sources);
    return data.lead_sources;
  },

  async updateProcedures({ commit }, procedures) {
    const { data } = await OpportunitiesAPI.updateCrmSettings({ procedures });
    commit('SET_PROCEDURES', data.procedures);
    return data.procedures;
  },
};

export default { namespaced: true, state, getters, mutations, actions };
